import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supplierModel } from '../../../models/supplierModel';
import { useSupplierAuth } from '../../../context/SupplierAuthContext';
import { getSettings } from '../../../models/settingsModel';
import { toast } from 'react-toastify';
import { API_BASE_URL } from '../../../config';
import '../../../styles/colors.css';

function SupplierQuotations() {
  const { supplier } = useSupplierAuth();
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState(''); // For immediate UI updates
  const [statusFilter, setStatusFilter] = useState('all');
  const [showDebugModal, setShowDebugModal] = useState(false);
  const [settings, setSettings] = useState(null);
  const searchTimeoutRef = useRef(null);

  // Debounced search function
  const debouncedSetSearchTerm = useCallback((value) => {
    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Set new timeout
    searchTimeoutRef.current = setTimeout(() => {
      setSearchTerm(value);
      setCurrentPage(1); // Reset to first page when searching
    }, 500);
  }, []);

  // Handle search input changes
  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);
    debouncedSetSearchTerm(value);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Fetch quotations with pagination
  const fetchQuotations = useCallback(async () => {
    try {
      setLoading(true);
      
      const params = {
        page: currentPage,
        pageSize: pageSize
      };
      
      if (searchTerm) params.search = searchTerm;
      
      const response = await supplierModel.getQuotations(params); 
      
      if (response.status === 1 || response.data) {
        // Handle the nested data structure from the API
        const responseData = response.data || response;
        const allQuotations = responseData.quotations || responseData.data || [];
        const meta = responseData.meta || {}; 
        
        // Client-side filtering for supplier if needed
        const supplierCode = String(supplier?.supplier_user_supplier_id || supplier?.BP_Code || '');
        let filteredQuotations = allQuotations;
        
        if (supplierCode && allQuotations.length > 0 && !meta.totalItems) {
          // If no server-side filtering and we have supplier code, filter client-side
          filteredQuotations = allQuotations.filter(quotation => 
            String(quotation.quotation_company_code) === supplierCode ||
            String(quotation.supplier_code) === supplierCode ||
            String(quotation.BP_Code) === supplierCode
          ); 
        }
        
        // Apply status filter if set
        if (statusFilter !== 'all') {
          filteredQuotations = filteredQuotations.filter(quotation => {
            const currentDate = new Date();
            const closingDate = quotation.procurement_closing ? new Date(quotation.procurement_closing + ' ' + (quotation.procurement_closing_time || '23:59:59')) : null;
            const isExpired = closingDate && currentDate > closingDate;
            
            switch (statusFilter) {
              case 'open':
                // RFQ still open for submissions
                return !isExpired;
              case 'closed':
                // RFQ closed (past closing date)
                return isExpired;
              default:
                return true;
            }
          });
        }
        
        setQuotations(filteredQuotations);
        setTotalCount(meta.totalItems || filteredQuotations.length);
      }
    } catch (error) {
      console.error('Error fetching quotations:', error);
      toast.error('Failed to load quotations');
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, searchTerm, statusFilter, supplier]);

  useEffect(() => {
    fetchQuotations();
  }, [fetchQuotations]);

  // Load settings for branding
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const settingsData = await getSettings();
        setSettings(settingsData);
      } catch (error) {
        console.error('Failed to load settings:', error);
      }
    };
    
    loadSettings();
  }, []);

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    
    try {
      // Check if it's a Unix timestamp
      if (typeof timestamp === 'number' || !isNaN(timestamp)) {
        return new Date(parseInt(timestamp) * 1000).toLocaleString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        });
      }
      
      return timestamp;
    } catch (error) {
      console.error('Error formatting date:', error);
      return timestamp;
    }
  };

  const handleDownload = async (quotation) => {
    try {
      // To download the PDF, we need to call the backend to get encrypted download parameters
      // The backend will encrypt the supplier ID, RFQ ID, procurement ID, and quotation ID
      // and return them so we can use the download endpoint
      
      const supplierCode = String(supplier?.supplier_user_supplier_id || supplier?.BP_Code || '');
      const procurementId = quotation.procurement_id || quotation.quotation_procurement_id;
      const quotationId = quotation.quotation_id;
      
      console.log('Download params:', { supplierCode, procurementId, quotationId });
      console.log('Full quotation object:', quotation);
      console.log('Full supplier object:', supplier);
      
      if (!supplierCode || !procurementId || !quotationId) {
        console.error('Missing required data:', { supplierCode, procurementId, quotationId });
        toast.error('Missing required data for download');
        return;
      }

      // Request encrypted download parameters from backend
      const response = await supplierModel.getQuotationDownloadParams({
        supplier_id: supplierCode,
        procurement_id: procurementId,
        quotation_id: quotationId
      });

      console.log('Download params response:', response);

      // Check for download_params in both response root and response.data
      const downloadParams = response.download_params || response.data?.download_params;
      
      if (response.status === 1 && downloadParams) {
        const params = downloadParams;
        const downloadUrl = `${API_BASE_URL}/api/procurement/supplier/quote/download?sid=${encodeURIComponent(params.sid)}&rfq=${encodeURIComponent(params.rfq)}&pid=${encodeURIComponent(params.pid)}&qid=${encodeURIComponent(params.qid)}`;
        console.log('Opening download URL:', downloadUrl);
        window.open(downloadUrl, '_blank');
        toast.success('Download started');
      } else {
        console.error('Invalid response:', response);
        toast.error(response.message || 'Failed to generate download link');
      }
    } catch (error) {
      console.error('Error downloading quotation:', error);
      console.error('Error details:', error.response?.data);
      toast.error(error.response?.data?.message || 'Failed to download quotation');
    }
  };

  // Function to get quotation status with styling
  const getQuotationStatus = (quotation) => {
    const currentDate = new Date();
    const closingDate = quotation.procurement_closing ? new Date(quotation.procurement_closing + ' ' + (quotation.procurement_closing_time || '23:59:59')) : null;
    const isExpired = closingDate && currentDate > closingDate;
    
    if (isExpired) {
      return { status: 'Closed', className: 'bg-gray-100 text-gray-800' };
    } else {
      return { status: 'Open', className: 'bg-green-100 text-green-800' };
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col items-center justify-center min-h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Loading quotations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-7xl mx-auto px-2 sm:px-4 lg:px-8 py-4 sm:py-8">
      {/* Header Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="p-4 sm:p-6">
          <div className="flex items-center justify-between">
            {/* Left side - Title and info */}
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <h1 className="text-xl font-semibold text-gray-900">My Quotations</h1>
                <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
                  <span>Submitted Quotations & Proposals</span>
                  <span>•</span>
                  <span>{totalCount} items</span>
                  <span>•</span>
                  <span>Page {currentPage} of {Math.ceil(totalCount / pageSize) || 1}</span>
                  <span>•</span>
                  <span>Showing {Math.min(pageSize, totalCount)} of {totalCount}</span>
                </div>
              </div>
            </div>
            
            {/* Right side - Search */}
            <div className="flex items-center gap-4">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search quotations..."
                  value={searchInput}
                  onChange={handleSearchInputChange}
                  className="w-64 pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                {searchInput && (
                  <button
                    onClick={() => {
                      // Clear timeout
                      if (searchTimeoutRef.current) {
                        clearTimeout(searchTimeoutRef.current);
                      }
                      setSearchInput('');
                      setSearchTerm('');
                      setCurrentPage(1);
                    }}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    <svg className="h-4 w-4 text-gray-400 hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
              
              {/* Debug Button - Only show in development mode */}
              {settings?.debug_mode === '1' && (
                <button
                  onClick={() => setShowDebugModal(true)}
                  className="text-xs font-bold px-3 py-2 rounded-lg border border-red-300 bg-red-50 text-red-700 hover:bg-red-100 transition"
                  title="Open Debug Information Modal"
                >
                  🐛 Debug
                </button>
              )}
            </div>
          </div>
          
          {/* Controls Row */}
          <div className="mt-6 pt-4 border-t border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600">Status:</label>
                <select 
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="all">All Status</option>
                  <option value="open">Open</option>
                  <option value="closed">Closed</option>
                </select>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">Show:</label>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(parseInt(e.target.value));
                  setCurrentPage(1);
                }}
                className="border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="10">10 per page</option>
                <option value="20">20 per page</option>
                <option value="50">50 per page</option>
                <option value="100">100 per page</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Quotations Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
        <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
          <table className="w-full divide-y divide-gray-200 table-fixed">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-2/5 min-w-0">
                  Project Name
                </th>
                <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/5 min-w-0">
                  Reference
                </th>
                <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6 min-w-0">
                  Status
                </th>
                <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6 min-w-0">
                  Submitted
                </th>
                <th className="px-3 sm:px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-1/12 min-w-0">
                  File
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {quotations.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center">
                      <svg className="w-12 h-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <p className="text-gray-500 text-lg">No quotations found.</p>
                      <p className="text-gray-400 text-sm mt-1">Your submitted quotations will appear here.</p>
                    </div>
                  </td>
                </tr>
              ) : (
                quotations.map((quotation, index) => {
                  const statusInfo = getQuotationStatus(quotation);
                  return (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-3 sm:px-4 py-3 truncate">
                      <span className="text-gray-900 font-medium text-sm block truncate" title={quotation.procurement_title}>{quotation.procurement_title}</span>
                    </td>
                    <td className="px-3 sm:px-4 py-3 text-sm text-gray-600 truncate">
                      <span className="block truncate" title={quotation.procurement_ref}>{quotation.procurement_ref}</span>
                    </td>
                    <td className="px-3 sm:px-4 py-3">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full whitespace-nowrap ${statusInfo.className}`}>
                        {statusInfo.status}
                      </span>
                    </td>
                    <td className="px-3 sm:px-4 py-3 text-sm text-gray-600 truncate">
                      <span className="block truncate">{formatDate(quotation.quotation_time)}</span>
                    </td>
                    <td className="px-3 sm:px-4 py-3 text-center">
                      <button
                        type="button"
                        className="inline-flex items-center justify-center w-8 h-8 text-blue-600 bg-blue-50 border border-blue-200 rounded hover:bg-blue-100 hover:border-blue-300 focus:ring-2 focus:ring-blue-500 focus:outline-none transition-colors"
                        onClick={() => handleDownload(quotation)}
                        title="Download quotation"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        {totalCount > 0 && (
          <div className="bg-gray-50 px-3 py-2 border-t border-gray-200 flex items-center justify-between text-xs">
            <div className="text-gray-500">
              Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} quotations
            </div>
            <div className="flex items-center space-x-1">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="px-2 py-1 border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-40 rounded text-xs"
              >
                First
              </button>
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-2 py-1 border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-40 rounded text-xs"
              >
                Previous
              </button>
              <span className="px-2 py-1 border border-gray-300 bg-white text-gray-700 font-medium text-xs">
                {currentPage} / {Math.ceil(totalCount / pageSize) || 1}
              </span>
              <button
                onClick={() => setCurrentPage(Math.min(Math.ceil(totalCount / pageSize) || 1, currentPage + 1))}
                disabled={currentPage >= Math.ceil(totalCount / pageSize)}
                className="px-2 py-1 border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-40 rounded text-xs"
              >
                Next
              </button>
              <button
                onClick={() => setCurrentPage(Math.ceil(totalCount / pageSize) || 1)}
                disabled={currentPage >= Math.ceil(totalCount / pageSize)}
                className="px-2 py-1 border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-40 rounded text-xs"
              >
                Last
              </button>
            </div>
          </div>
        )}
      </div>

      {quotations.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="border-b border-gray-200 bg-gray-50 px-6 py-4">
            <h5 className="text-lg font-semibold text-gray-900">Summary</h5>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center p-4 border border-gray-200 rounded-lg bg-gray-50">
                <div className="text-3xl font-bold text-blue-600 mb-2">{quotations.length}</div>
                <p className="text-sm text-gray-600 font-medium">Total Quotations</p>
              </div>
              <div className="text-center p-4 border border-gray-200 rounded-lg bg-gray-50">
                <div className="text-3xl font-bold text-green-600 mb-2">
                  {quotations.filter(q => q.requisition_po).length}
                </div>
                <p className="text-sm text-gray-600 font-medium">Submitted</p>
              </div>
              <div className="text-center p-4 border border-gray-200 rounded-lg bg-gray-50">
                <div className="text-3xl font-bold text-yellow-600 mb-2">
                  {quotations.filter(q => !q.requisition_po).length}
                </div>
                <p className="text-sm text-gray-600 font-medium">Pending</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Debug Modal */}
      {settings?.debug_mode === '1' && (
        <DebugModal 
          show={showDebugModal}
          onHide={() => setShowDebugModal(false)}
          quotations={quotations}
          supplier={supplier}
          allData={{
            quotations,
            supplier,
            settings,
            loading,
            currentPage,
            pageSize,
            totalCount,
            searchTerm,
            statusFilter,
            token: localStorage.getItem('supplier_token')
          }}
        />
      )}
    </div>
  );
}

// Debug Modal Component for Quotations
const DebugModal = ({ show, onHide, quotations, supplier, allData }) => {
  const [selectedSection, setSelectedSection] = useState('overview');
  
  if (!show) return null;

  const dataStructure = {
    overview: {
      title: 'Overview',
      icon: '📋',
      data: {
        supplier_name: supplier?.supplier_user_name || supplier?.Contact_Person || supplier?.BP_Name,
        supplier_email: supplier?.supplier_user_email || supplier?.E_Mail,
        supplier_id: supplier?.supplier_user_supplier_id || supplier?.VendorID || supplier?.BP_Code,
        quotations_count: quotations?.length || 0,
        total_count: allData?.totalCount || 0,
        current_page: allData?.currentPage || 1,
        page_size: allData?.pageSize || 20,
        search_term: allData?.searchTerm || '',
        status_filter: allData?.statusFilter || 'all',
        token_exists: !!localStorage.getItem('supplier_token'),
        loading_state: allData?.loading
      }
    },
    quotations: {
      title: 'Quotations Data',
      icon: '📄',
      data: quotations || []
    },
    supplier_context: {
      title: 'Supplier Context',
      icon: '👤',
      data: supplier || {}
    },
    settings: {
      title: 'Settings',
      icon: '⚙️',
      data: {
        settings_loaded: !!allData?.settings,
        site_logo_preview: allData?.settings?.data?.site_logo_preview || 'Not available',
        site_name: allData?.settings?.data?.site_name || 'Not available',
        raw_settings: allData?.settings || {}
      }
    },
    authentication: {
      title: 'Authentication',
      icon: '🔐',
      data: {
        token: allData?.token ? 'Present' : 'Missing',
        token_length: allData?.token?.length || 0,
        localStorage_keys: Object.keys(localStorage),
        supplier_login_status: supplier ? 'Logged in' : 'Not logged in'
      }
    },
    pagination: {
      title: 'Pagination Info',
      icon: '📊',
      data: {
        current_page: allData?.currentPage,
        page_size: allData?.pageSize,
        total_count: allData?.totalCount,
        total_pages: Math.ceil((allData?.totalCount || 0) / (allData?.pageSize || 20)),
        search_term: allData?.searchTerm,
        status_filter: allData?.statusFilter,
        displayed_items: quotations?.length || 0
      }
    },
    raw: {
      title: 'Raw Data',
      icon: '🔧',
      data: allData
    }
  };

  return (
    <div className={`fixed inset-0 z-50 ${show ? 'block' : 'hidden'}`}>
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onHide}></div>
      <div className="relative bg-white rounded-lg shadow-xl max-w-7xl mx-auto mt-4 mb-4 h-[95vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-red-600 text-white p-4 flex justify-between items-center flex-shrink-0">
          <h3 className="text-lg font-semibold flex items-center">
            <svg className="inline-block mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Debug Modal - Quotations Component
          </h3>
          <button onClick={onHide} className="text-white hover:text-gray-200">
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Sidebar */}
          <div className="w-64 bg-gray-50 border-r border-gray-200 flex-shrink-0 overflow-y-auto">
            <div className="p-4">
              <h4 className="text-sm font-semibold text-gray-900 mb-3">Debug Sections</h4>
              <div className="space-y-1">
                {Object.entries(dataStructure).map(([key, section]) => (
                  <button
                    key={key}
                    onClick={() => setSelectedSection(key)}
                    className={`w-full text-left px-3 py-2 text-sm rounded-md flex items-center ${
                      selectedSection === key
                        ? 'bg-blue-100 text-blue-700 font-medium'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <span className="mr-2">{section.icon}</span>
                    {section.title}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            <div className="p-6">
              <div className="mb-4">
                <h4 className="text-lg font-semibold text-gray-900 flex items-center">
                  <span className="mr-2">{dataStructure[selectedSection]?.icon}</span>
                  {dataStructure[selectedSection]?.title}
                </h4>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-4">
                <pre className="text-sm text-gray-800 whitespace-pre-wrap overflow-auto max-h-96">
                  {JSON.stringify(dataStructure[selectedSection]?.data, null, 2)}
                </pre>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupplierQuotations;
